import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Download, FileText } from 'lucide-react';

const ANY_VALUE = '__any__';

const COST_REPORT_OPTIONAL_COLUMNS = [
  { key: 'total_cost',       label: 'Total Cost' },
  { key: 'margin',           label: 'Margin' },
  { key: 'invoice_with_vat', label: 'Total Job Amount (With VAT)' },
  { key: 'reef_staff',       label: 'Total Number of Reef Staff' },
  { key: 'days_taken',       label: 'Number of Days Taken' },
  { key: 'property_type',    label: 'Property Type' },
  { key: 'apartment_size',   label: 'Apartment Size' },
  { key: 'date',             label: 'Date' },
  { key: 'moving_date',      label: 'Moving Date' },
  { key: 'supervisor',       label: 'Supervisor Name' },
  { key: 'special_remarks',  label: 'Special Remarks' },
];

export default function Reports() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [costItems, setCostItems] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [selectedCostItems, setSelectedCostItems] = useState([]);
  const [selectedOptionalCols, setSelectedOptionalCols] = useState([]);

  const [dateRange, setDateRange] = useState({ start_date: '', end_date: '' });
  const [filters, setFilters] = useState({
    job_done_by: ANY_VALUE,
    painting_included: ANY_VALUE,
    curtain_fixing_included: ANY_VALUE,
    storage_included: ANY_VALUE,
    salesperson_id: ANY_VALUE,
    supervisor_name: ANY_VALUE,
  });

  const [costDateRange, setCostDateRange] = useState({ start_date: '', end_date: '' });
  const [costFilters, setCostFilters] = useState({
    job_done_by: ANY_VALUE,
    painting_included: ANY_VALUE,
    curtain_fixing_included: ANY_VALUE,
    storage_included: ANY_VALUE,
    salesperson_id: ANY_VALUE,
    supervisor_name: ANY_VALUE,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ciRes, spRes, supRes] = await Promise.all([
          axios.get(`${API}/cost-items`),
          axios.get(`${API}/salespersons`),
          axios.get(`${API}/supervisors`),
        ]);
        setCostItems(ciRes.data || []);
        setSalespersons(spRes.data || []);
        setSupervisors(supRes.data || []);
      } catch (err) {
        console.warn('Reports: failed to load optional master data', err);
      }
    };
    fetchData();
  }, []);

  const toggleCostItem = (name) => {
    setSelectedCostItems(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const selectAllCostItems = () => {
    if (selectedCostItems.length === costItems.length) {
      setSelectedCostItems([]);
    } else {
      setSelectedCostItems(costItems.map(c => c.name));
    }
  };

  const toggleOptionalCol = (key) => {
    setSelectedOptionalCols(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const buildFilterParams = (f) => {
    const params = {};
    if (f.job_done_by !== ANY_VALUE) params.job_done_by = f.job_done_by;
    if (f.painting_included !== ANY_VALUE) params.painting_included = f.painting_included;
    if (f.curtain_fixing_included !== ANY_VALUE) params.curtain_fixing_included = f.curtain_fixing_included;
    if (f.storage_included !== ANY_VALUE) params.storage_included = f.storage_included;
    if (f.salesperson_id !== ANY_VALUE) params.salesperson_id = f.salesperson_id;
    if (f.supervisor_name !== ANY_VALUE) params.supervisor_name = f.supervisor_name;
    return params;
  };

  const handleDownload = async (format) => {
    if (!dateRange.start_date || !dateRange.end_date) {
      toast.error('Please select both start and end dates');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/download`, {
        params: {
          format,
          start_date: dateRange.start_date,
          end_date: dateRange.end_date,
          ...buildFilterParams(filters),
        },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `jobs_report.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setLoading(false);
    }
  };

  const handleCostReport = async (format) => {
    if (!costDateRange.start_date || !costDateRange.end_date) {
      toast.error('Please select both start and end dates');
      return;
    }
    if (selectedCostItems.length === 0) {
      toast.error('Please select at least one cost item');
      return;
    }
    setLoading(true);
    try {
      const params = {
        format,
        start_date: costDateRange.start_date,
        end_date: costDateRange.end_date,
        cost_items: selectedCostItems.join(','),
        ...buildFilterParams(costFilters),
      };
      if (selectedOptionalCols.length > 0) {
        params.columns = selectedOptionalCols.join(',');
      }
      const response = await axios.get(`${API}/reports/cost-report`, {
        params,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cost_report.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Cost report downloaded successfully');
    } catch (error) {
      toast.error('Failed to download cost report');
    } finally {
      setLoading(false);
    }
  };

  const renderFilterBlock = (testPrefix, value, setValue) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
      <div className="space-y-2">
        <Label>Salesperson</Label>
        <Select
          value={value.salesperson_id}
          onValueChange={(v) => setValue({ ...value, salesperson_id: v })}
        >
          <SelectTrigger data-testid={`${testPrefix}-salesperson-filter`}>
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY_VALUE}>All Salespersons</SelectItem>
            {salespersons.map((sp) => (
              <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Job Done By</Label>
        <Select
          value={value.job_done_by}
          onValueChange={(v) => setValue({ ...value, job_done_by: v })}
        >
          <SelectTrigger data-testid={`${testPrefix}-job-done-by-filter`}>
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY_VALUE}>All</SelectItem>
            <SelectItem value="Reef Team">Reef Team</SelectItem>
            <SelectItem value="Third Party">Third Party</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Painting Services Included?</Label>
        <Select
          value={value.painting_included}
          onValueChange={(v) => setValue({ ...value, painting_included: v })}
        >
          <SelectTrigger data-testid={`${testPrefix}-painting-filter`}>
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY_VALUE}>All</SelectItem>
            <SelectItem value="Yes">Yes</SelectItem>
            <SelectItem value="No">No</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>New Curtain Service Included?</Label>
        <Select
          value={value.curtain_fixing_included}
          onValueChange={(v) => setValue({ ...value, curtain_fixing_included: v })}
        >
          <SelectTrigger data-testid={`${testPrefix}-curtain-filter`}>
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY_VALUE}>All</SelectItem>
            <SelectItem value="Yes">Yes</SelectItem>
            <SelectItem value="No">No</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Storage Service Included?</Label>
        <Select
          value={value.storage_included}
          onValueChange={(v) => setValue({ ...value, storage_included: v })}
        >
          <SelectTrigger data-testid={`${testPrefix}-storage-filter`}>
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY_VALUE}>All</SelectItem>
            <SelectItem value="Yes">Yes</SelectItem>
            <SelectItem value="No">No</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Supervisor Name</Label>
        <Select
          value={value.supervisor_name}
          onValueChange={(v) => setValue({ ...value, supervisor_name: v })}
        >
          <SelectTrigger data-testid={`${testPrefix}-supervisor-filter`}>
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY_VALUE}>All Supervisors</SelectItem>
            {supervisors.map((s) => (
              <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="reports-page">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-10 backdrop-blur-md bg-white/80">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="reports-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-slate-900 ml-4">Reports</h1>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto p-6 md:p-8 space-y-6">
        {/* General Job Report */}
        <Card className="shadow-sm border-slate-200">
          <CardHeader>
            <CardTitle>Job Report</CardTitle>
            <CardDescription>Download a summary of all jobs for a date range with optional filters</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={dateRange.start_date}
                  onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                  data-testid="start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={dateRange.end_date}
                  onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                  data-testid="end-date-input"
                />
              </div>
            </div>

            {renderFilterBlock('job', filters, setFilters)}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <Button
                onClick={() => handleDownload('pdf')}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white h-14"
                data-testid="download-pdf-btn"
              >
                <FileText className="w-5 h-5 mr-2" />
                Download PDF
              </Button>
              <Button
                onClick={() => handleDownload('excel')}
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-14"
                data-testid="download-excel-btn"
              >
                <Download className="w-5 h-5 mr-2" />
                Download Excel
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Cost-Wise Report */}
        <Card className="shadow-sm border-slate-200">
          <CardHeader>
            <CardTitle>Cost-Wise Report</CardTitle>
            <CardDescription>Select specific cost items and get a detailed breakdown per job</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={costDateRange.start_date}
                  onChange={(e) => setCostDateRange({ ...costDateRange, start_date: e.target.value })}
                  data-testid="cost-start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={costDateRange.end_date}
                  onChange={(e) => setCostDateRange({ ...costDateRange, end_date: e.target.value })}
                  data-testid="cost-end-date-input"
                />
              </div>
            </div>

            {renderFilterBlock('cost', costFilters, setCostFilters)}

            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">Select Cost Items</Label>
                <Button variant="ghost" size="sm" onClick={selectAllCostItems} data-testid="select-all-cost-items">
                  {selectedCostItems.length === costItems.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 p-4 border border-slate-200 rounded-lg bg-white max-h-64 overflow-y-auto" data-testid="cost-items-list">
                {costItems.map((item) => (
                  <div key={item.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`cost-${item.id}`}
                      checked={selectedCostItems.includes(item.name)}
                      onCheckedChange={() => toggleCostItem(item.name)}
                      data-testid={`cost-item-${item.id}`}
                    />
                    <label
                      htmlFor={`cost-${item.id}`}
                      className="text-sm cursor-pointer leading-tight"
                    >
                      {item.name}
                    </label>
                  </div>
                ))}
                {costItems.length === 0 && (
                  <p className="text-sm text-slate-500 col-span-full">No cost items configured. Add them in Admin Settings.</p>
                )}
              </div>
              {selectedCostItems.length > 0 && (
                <p className="text-xs text-slate-500 mt-2">{selectedCostItems.length} item(s) selected</p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">Additional Columns to Include</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedOptionalCols(
                    selectedOptionalCols.length === COST_REPORT_OPTIONAL_COLUMNS.length ? [] : COST_REPORT_OPTIONAL_COLUMNS.map(c => c.key)
                  )}
                  data-testid="select-all-optional-cols"
                >
                  {selectedOptionalCols.length === COST_REPORT_OPTIONAL_COLUMNS.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 p-4 border border-slate-200 rounded-lg bg-white" data-testid="optional-cols-list">
                {COST_REPORT_OPTIONAL_COLUMNS.map((c) => (
                  <div key={c.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`optcol-${c.key}`}
                      checked={selectedOptionalCols.includes(c.key)}
                      onCheckedChange={() => toggleOptionalCol(c.key)}
                      data-testid={`optional-col-${c.key}`}
                    />
                    <label htmlFor={`optcol-${c.key}`} className="text-sm cursor-pointer leading-tight">
                      {c.label}
                    </label>
                  </div>
                ))}
              </div>
              {selectedOptionalCols.length > 0 && (
                <p className="text-xs text-slate-500 mt-2">{selectedOptionalCols.length} extra column(s) selected</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <Button
                onClick={() => handleCostReport('pdf')}
                disabled={loading || selectedCostItems.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white h-14"
                data-testid="download-cost-pdf-btn"
              >
                <FileText className="w-5 h-5 mr-2" />
                Download Cost Report PDF
              </Button>
              <Button
                onClick={() => handleCostReport('excel')}
                disabled={loading || selectedCostItems.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-14"
                data-testid="download-cost-excel-btn"
              >
                <Download className="w-5 h-5 mr-2" />
                Download Cost Report Excel
              </Button>
            </div>

            {loading && (
              <div className="text-center text-slate-600 py-4">Generating report...</div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
